<script setup>
// custom (CRM v2 · Fase 2): multiselect com chips, usado na Tela A para Agentes e
// Caixas de entrada. Replica o controle das refs (campo com pills removíveis + ✕,
// chevron à direita, dropdown de opções). White-mode, tokens n-*, pt-BR locked.
//
// Controlado por v-model (array de ids). As opções vêm de fora; cada uma é
// { id, label, icon? } onde icon é uma classe iconify (ex. 'i-lucide-message-circle')
// ou um hex de cor para a bolinha. Genérico o bastante para agentes e inboxes.
import { ref, computed } from 'vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  // [{ id, label, icon? }]
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Selecionar…' },
  emptyText: { type: String, default: 'Nada disponível.' },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);

const selectedOptions = computed(() =>
  props.modelValue
    .map(id => props.options.find(o => o.id === id))
    .filter(Boolean)
);

const availableOptions = computed(() =>
  props.options.filter(o => !props.modelValue.includes(o.id))
);

function add(id) {
  if (props.modelValue.includes(id)) return;
  emit('update:modelValue', [...props.modelValue, id]);
}

function remove(id) {
  emit(
    'update:modelValue',
    props.modelValue.filter(v => v !== id)
  );
}

function toggle() {
  open.value = !open.value;
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <div v-on-clickaway="() => (open = false)" class="relative">
    <!-- Control -->
    <button
      type="button"
      class="w-full min-h-[40px] px-2 py-1.5 flex items-center gap-1.5 flex-wrap text-left rounded-lg border bg-n-background transition-colors duration-mj-toque"
      :class="[
        open
          ? 'border-n-slate-8 ring-2 ring-n-slate-7/40'
          : 'border-n-weak hover:border-n-slate-7',
      ]"
      @click="toggle"
    >
      <!-- Chips -->
      <span
        v-for="opt in selectedOptions"
        :key="opt.id"
        class="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-n-alpha-2 border border-n-weak text-mj-12 font-500 text-n-slate-12"
        @click.stop
      >
        <span
          v-if="opt.icon && opt.icon.startsWith('i-')"
          class="size-3 text-n-slate-10"
          :class="opt.icon"
        />
        <span
          v-else-if="opt.icon"
          class="size-2 rounded-full"
          :style="{ backgroundColor: opt.icon }"
        />
        <span class="max-w-[160px] truncate">{{ opt.label }}</span>
        <span
          role="button"
          tabindex="0"
          class="size-4 inline-flex items-center justify-center rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer"
          title="Remover"
          @click.stop="remove(opt.id)"
          @keydown.enter.prevent="remove(opt.id)"
        >
          <span class="i-lucide-x size-3" />
        </span>
      </span>

      <span
        v-if="selectedOptions.length === 0"
        class="text-mj-13 font-400 text-n-slate-10 px-1"
      >
        {{ placeholder }}
      </span>

      <span class="flex-1" />
      <span
        class="i-lucide-chevron-down size-4 text-n-slate-9 shrink-0 transition-transform duration-mj-toque"
        :class="[open && 'rotate-180']"
      />
    </button>

    <!-- Dropdown. Nasce do gatilho (origin-top, scale 0.96 → 1); saída mais curta
         que a entrada. motion-reduce corta o movimento. -->
    <Transition
      enter-active-class="transition-[transform,opacity] duration-mj-toque ease-mj motion-reduce:transition-none"
      enter-from-class="opacity-0 scale-[0.96]"
      leave-active-class="transition-[transform,opacity] duration-mj-toque ease-mj motion-reduce:transition-none"
      leave-to-class="opacity-0 scale-[0.98]"
    >
      <div
        v-if="open"
        class="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto crm-scrollbar-thin origin-top bg-n-background border border-n-weak rounded-card shadow-mj-4 flex flex-col py-1"
        @click.stop
      >
        <button
          v-for="opt in availableOptions"
          :key="opt.id"
          type="button"
          class="px-3 py-2 flex items-center gap-2 text-left text-mj-13 font-400 text-n-slate-12 hover:bg-n-alpha-1 transition-colors duration-mj-toque"
          @click="add(opt.id)"
        >
          <span
            v-if="opt.icon && opt.icon.startsWith('i-')"
            class="size-3.5 text-n-slate-10 shrink-0"
            :class="opt.icon"
          />
          <span
            v-else-if="opt.icon"
            class="size-2.5 rounded-full shrink-0"
            :style="{ backgroundColor: opt.icon }"
          />
          <span class="truncate">{{ opt.label }}</span>
        </button>

        <p
          v-if="availableOptions.length === 0"
          class="px-3 py-2.5 text-mj-12 font-400 text-n-slate-10"
        >
          {{ selectedOptions.length ? 'Tudo selecionado.' : emptyText }}
        </p>
      </div>
    </Transition>
  </div>
</template>
